/** 튜토리얼 타깃 요소 탐색 유틸. 같은 클래스를 공유하는 탭·토글 버튼은 `text:` 셀렉터로 텍스트까지 구분한다. */

import { clientRectToLayoutRect, getLayoutViewportSize } from '../utils/pageZoom';

export function isTutorialTargetVisible(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  const style = window.getComputedStyle(el);
  if (style.visibility === 'hidden' || style.display === 'none') return false;
  if (Number(style.opacity) === 0) return false;
  return true;
}

/**
 * 셀렉터 형식:
 * - 일반 CSS 셀렉터: 화면에 보이는 매칭 요소를 모두 반환 (학년 칩·옵션 버튼 등 복수 선택 UI용)
 * - `text:라벨@BASE_SELECTOR`: BASE_SELECTOR로 찾은 요소 중 텍스트가 정확히 일치하는 것만 사용
 * - 쉼표로 여러 셀렉터를 나열하면 결과를 합침 (`text:`와 CSS를 섞어 써도 됨)
 */
export function resolveTutorialTargets(selector: string): HTMLElement[] {
  const parts = splitTutorialSelectors(selector);
  if (parts.length > 1) {
    const seen = new Set<HTMLElement>();
    const out: HTMLElement[] = [];
    for (const part of parts) {
      for (const el of resolveTutorialTargets(part)) {
        if (seen.has(el)) continue;
        seen.add(el);
        out.push(el);
      }
    }
    return out;
  }

  const textForm = selector.match(/^text:(.+?)@(.+)$/);
  if (textForm) {
    const label = textForm[1].trim();
    const base = textForm[2].trim();
    let candidates: HTMLElement[] = [];
    try {
      candidates = Array.from(document.querySelectorAll<HTMLElement>(base)).filter(
        (el) => (el.textContent || '').trim() === label
      );
    } catch {
      return [];
    }
    // base가 버튼/링크 안의 라벨 span(예: 전체·축약 라벨을 나눠 둔 케이스)이면
    // 직사각형 버튼 전체가 하이라이트·클릭 대상이 되도록 가장 가까운 클릭 가능 조상으로 올려 잡는다.
    candidates = candidates.map((el) => {
      if (el.matches('button, a, [role="button"]')) return el;
      return el.closest<HTMLElement>('button, a, [role="button"]') ?? el;
    });
    const visible = candidates.filter(isTutorialTargetVisible);
    return visible.length > 0 ? visible : candidates.slice(0, 1);
  }
  try {
    const all = Array.from(document.querySelectorAll<HTMLElement>(selector));
    const visible = all.filter(isTutorialTargetVisible);
    return visible;
  } catch {
    return [];
  }
}

/** 쉼표로 셀렉터를 나누되, `text:라벨@a, b`처럼 베이스 안의 쉼표는 유지한다. */
export function splitTutorialSelectors(selector: string): string[] {
  const raw = selector.trim();
  if (!raw.includes(',')) return [raw];
  const parts: string[] = [];
  let buf = '';
  let inTextForm = false;
  for (let i = 0; i < raw.length; i += 1) {
    const ch = raw[i];
    if (!inTextForm && raw.slice(i).startsWith('text:')) {
      inTextForm = true;
      buf += ch;
      continue;
    }
    if (inTextForm && ch === '@') {
      inTextForm = false;
      buf += ch;
      continue;
    }
    if (ch === ',' && !inTextForm) {
      const trimmed = buf.trim();
      if (trimmed) parts.push(trimmed);
      buf = '';
      continue;
    }
    buf += ch;
  }
  const trimmed = buf.trim();
  if (trimmed) parts.push(trimmed);
  return parts.length > 0 ? parts : [raw];
}

/** 단일 타깃이 필요한 경우 — 보이는 첫 요소 */
export function resolveTutorialTarget(selector: string): HTMLElement | null {
  return resolveTutorialTargets(selector)[0] ?? null;
}

/**
 * 화면 표시 여부와 관계없이 DOM에 셀렉터 매칭이 있는지만 본다.
 * 모달 오픈 직후 opacity/크기 측정 전에 advanceWhenSelectorExists가 놓치는 경우를 보완한다.
 */
export function tutorialSelectorExistsInDom(selector: string): boolean {
  const parts = splitTutorialSelectors(selector);
  for (const part of parts) {
    const textForm = part.match(/^text:(.+?)@(.+)$/);
    if (textForm) {
      const label = textForm[1].trim();
      const base = textForm[2].trim();
      try {
        const hit = Array.from(document.querySelectorAll<HTMLElement>(base)).some(
          (el) => (el.textContent || '').trim() === label && el.isConnected
        );
        if (hit) return true;
      } catch {
        /* ignore invalid selector */
      }
      continue;
    }
    try {
      const hit = Array.from(document.querySelectorAll<HTMLElement>(part)).some((el) => el.isConnected);
      if (hit) return true;
    } catch {
      /* ignore invalid selector */
    }
  }
  return false;
}

const TUTORIAL_CLICKABLE_ANCESTOR_SELECTOR = 'button, a, [role="button"], [role="tab"], [role="menuitem"]';

export function isTutorialClickOnTarget(
  targets: HTMLElement | HTMLElement[] | null,
  clicked: EventTarget | null
): boolean {
  if (!targets || !(clicked instanceof Node)) return false;
  const list = Array.isArray(targets) ? targets : [targets];
  return list.some((target) => {
    // 타깃 자신·타깃 안 클릭은 허용
    if (target === clicked || target.contains(clicked)) return true;
    // 타깃이 버튼 안 아이콘/라벨인 경우, 그 클릭 가능 조상(버튼 등) 클릭만 허용한다.
    // overlay·backdrop처럼 넓은 조상까지 허용하면 "모달 밖 클릭 = 닫기"가 뚫린다.
    if (!(clicked instanceof Element)) return false;
    const clickable = target.closest(TUTORIAL_CLICKABLE_ANCESTOR_SELECTOR);
    return Boolean(clickable && (clickable === clicked || clickable.contains(clicked)));
  });
}

/**
 * 튜토리얼에서 설명하던 모달/팝업을 다음 단계로 넘어가기 전에 닫을 때 시도할 "닫기" 후보 셀렉터.
 * 모달마다 닫는 방식이 달라(전용 닫기 버튼 / 바깥(backdrop) 클릭 / Escape) 순서대로 시도한다.
 * (예: 학급 선택·색상·확인창·행사/관리 모달은 Escape가 먹지 않고 버튼·바깥 클릭으로만 닫힘)
 */
export const TUTORIAL_OVERLAY_CLOSE_BUTTON_SELECTOR = [
  '.cp-modal-footer .cp-btn-secondary',
  '[aria-label="닫기"]',
  '[aria-label="달력 닫기"]',
  '.cp-dash-year-cal-modal-close',
  '.cp-profile-img-modal-close',
  '.cp-profile-img-btn.is-cancel',
  '.cp-program-note-popup-actions > .cp-program-note-confirm-btn:first-child',
  '.cp-program-unit-popup-actions > .cp-program-note-confirm-btn:first-child',
  '.cp-hours-adjust-checklist-float-close',
  '.cp-btn-secondary',
  '.cp-apn-create-btn-cancel',
  '.cp-apn-check-modal-close',
  '.cp-apn-tbl-close',
  '[aria-label="모달 닫기"]',
  '[aria-label="과제 체크 닫기"]',
  '.cp-apn-reward-result-ok',
  '.cp-pin-modal-close',
  '.cp-pin-tt-modal-close',
  '.cp-pin-history-close',
  '.cp-weekly-learning-guide-modal-close',
].join(', ');

const TUTORIAL_OVERLAY_BACKDROP_SELECTOR = [
  '.cp-modal-backdrop',
  '.cp-dash-year-cal-backdrop',
  '.cp-profile-img-modal-overlay',
  '.cp-apn-create-overlay-dim',
  '.cp-apn-create-overlay-scroll',
  '.cp-apn-check-modal-dim',
  '.cp-apn-check-modal-scroll',
  '.cp-apn-modal-overlay',
  '.cp-apn-reach-overlay',
  '.cp-apn-tbl-overlay',
  '.cp-apn-incomplete-overlay',
  '.cp-apn-reward-result-overlay',
  '.cp-pin-bulk-overlay',
  '.cp-pin-tt-modal-overlay',
  '.cp-pin-cal-overlay',
  '.cp-pin-pts-overlay',
  '.cp-pin-confirm-overlay',
  '.cp-pc-manage-overlay',
  '.cp-weekly-learning-guide-modal-backdrop',
].join(', ');

/** 닫기로 쓰면 안 되는 secondary(양식 저장·데이터 반영 등 툴바 버튼) */
function isTutorialOverlayNonCloseSecondary(btn: HTMLElement): boolean {
  if (btn.matches('[data-tutorial-ep="web-doc-save-template"], [data-tutorial-ep="web-doc-apply-plan"]')) {
    return true;
  }
  if (
    btn.matches(
      '.cp-evaluation-plan-web-doc-template-save-btn, .cp-evaluation-plan-web-doc-template-apply-btn'
    )
  ) {
    return true;
  }
  if (btn.closest('.cp-evaluation-plan-web-doc-template-toolbar-actions')) return true;
  return false;
}

/** 오버레이 요소 하나를 최대한 안전하게 닫는다(전용 버튼 → 바깥(backdrop) 클릭 → Escape 순으로 시도). */
export function closeTutorialOverlayElement(el: HTMLElement): void {
  if (!el.isConnected) return;

  // querySelector(콤마 셀렉터)는 "셀렉터 우선순위"가 아니라 DOM 순이라,
  // 툴바의 .cp-btn-secondary(양식 저장)가 footer 취소보다 먼저 잡힐 수 있다.
  // 전용 취소 훅·footer 취소를 먼저 시도한다.
  const preferredCancelSelectors = [
    '[data-tutorial-ep="web-doc-cancel"]',
    '[data-tutorial-ep="sheet-cancel"]',
    '[data-tutorial-ws="cancel"]',
    '.cp-modal-footer .cp-btn-secondary',
  ];
  for (const sel of preferredCancelSelectors) {
    const btn = el.querySelector<HTMLElement>(sel);
    if (btn && isTutorialTargetVisible(btn) && !isTutorialOverlayNonCloseSecondary(btn)) {
      btn.click();
      return;
    }
  }

  const closeCandidates = Array.from(
    el.querySelectorAll<HTMLElement>(TUTORIAL_OVERLAY_CLOSE_BUTTON_SELECTOR)
  );
  for (const closeButton of closeCandidates) {
    if (!isTutorialTargetVisible(closeButton)) continue;
    if (isTutorialOverlayNonCloseSecondary(closeButton)) continue;
    closeButton.click();
    return;
  }

  const backdrop = el.closest<HTMLElement>(TUTORIAL_OVERLAY_BACKDROP_SELECTOR);
  if (backdrop && backdrop !== el) {
    // 일부 모달은 onClick이 아니라 onMouseDown으로만 닫힌다.
    backdrop.dispatchEvent(
      new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window })
    );
    backdrop.click();
    return;
  }
  // document가 아닌 오버레이 요소에 Escape를 보내 e.target이 Element가 되게 한다.
  // (document에 보내면 target.closest가 없어 전역 keydown 핸들러가 깨질 수 있다)
  el.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true })
  );
}

/** 여러 오버레이를 닫을 때는 나중에 열린(중첩된) 것부터 역순으로 닫는다. */
export function closeTutorialOverlayElements(els: HTMLElement[]): void {
  [...els].reverse().forEach((el) => closeTutorialOverlayElement(el));
}

/**
 * 조작 가능한 모달 단계에서 중첩으로 열릴 수 있는 오버레이 루트.
 * (규칙 3: 모달에서 새로 열린 모달·팝업도 하이라이트)
 */
export const TUTORIAL_STACKED_OVERLAY_SELECTOR = [
  '.cp-modal-backdrop .cp-modal',
  '.cp-dash-year-cal-modal',
  '.cp-program-cross-popup',
  '.cp-program-note-popup',
  '.cp-program-unit-popup',
  '.cp-program-eval-dropdown',
  '.cp-class-picker-modal',
  '.cp-leave-modal-box',
  '[data-tutorial-color-modal-type]',
  '.cp-profile-img-modal',
  '.cp-member-photo-upload-modal',
  '.cp-hours-adjust-checklist-float',
  '.cp-apn-create-modal',
  '.cp-apn-set-modal',
  '.cp-apn-check-modal-panel',
  '.cp-apn-reach-modal',
  '.cp-apn-tbl-modal',
  '.cp-apn-incomplete-modal',
  '.cp-apn-reward-result-modal',
  '.cp-pin-settings-modal',
  '.cp-pin-item-modal',
  '.cp-pin-level-modal',
  '.cp-pin-bulk-modal',
  '.cp-pin-history-modal',
  '.cp-pin-store-modal',
  '.cp-pin-item-inv-modal',
  '.cp-pin-item-grant-modal',
  '.cp-pin-confirm',
  '.cp-pc-manage-modal',
  '.cp-weekly-learning-guide-modal',
  '.cp-todo-note-item-add-overlay',
  '.cp-todo-note-category-modal',
  '.cp-todo-share-modal',
  '.cp-budget-note-modal',
  '.cp-budget-note-meta-cols-menu-pop',
  '.cp-budget-note-row-menu-pop',
  '.cp-class-lesson-alarm-settings-root',
  '.cp-class-lesson-alarm-popup-overlay',
  '.cp-edu-timer-overlay',
  '.cp-slide-dice-overlay',
  '.cp-program-slide-dice-modal',
  '.cp-program-slide-bgm-modal',
  '.cp-program-slide-timer-modal',
  '[data-tutorial-pres="bgm-modal"]',
  '[data-tutorial-slide="timer-modal"]',
].join(', ');

/**
 * 페이지 이동 중 "저장하지 않은 변경사항" 확인을 위해 어느 화면에서나 예고 없이 뜰 수 있는
 * 전역 확인 모달(MainLayout). 특정 단계의 targetSelector가 아니어도 항상 하이라이트·조작
 * 가능해야 튜토리얼이 막히지 않는다.
 */
export const TUTORIAL_LEAVE_CONFIRM_SELECTOR = [
  '[aria-labelledby="cp-program-leave-modal-title"] .cp-leave-modal-box',
  '[aria-labelledby="cp-leave-modal-title"] .cp-leave-modal-box',
].join(', ');

/**
 * 튜토리얼이 모달을 자동으로 닫을 때 이어 뜨는 "변경사항 확인"류 창을 저장 없이 닫는다.
 * (저장하지 않고 나가기 / 확인 모달의 취소 등)
 */
export function dismissTutorialUnsavedConfirmOverlays(): void {
  const discardByText = [
    'text:저장하지 않고 이동@.cp-leave-modal-box button',
    'text:저장하지 않고 나가기@.cp-leave-modal-box button',
    // 포인트·아이템·레벨 설정 모달의 변경 확인: 「닫기」로 저장 없이 종료
    'text:닫기@.cp-pin-confirm button',
  ];
  for (const sel of discardByText) {
    const btn = resolveTutorialTargets(sel)[0];
    if (btn) {
      btn.click();
      return;
    }
  }

  for (const box of resolveTutorialTargets(TUTORIAL_LEAVE_CONFIRM_SELECTOR)) {
    const secondary = box.querySelector<HTMLElement>('.cp-modal-footer .cp-btn-secondary');
    if (secondary && isTutorialTargetVisible(secondary)) {
      secondary.click();
      return;
    }
  }

  // 기준안 웹문서: 양식 이름 저장/수정 모달이 열려 있으면 취소로 닫는다.
  const templateNameCancel = resolveTutorialTargets(
    '.cp-evaluation-plan-web-doc-template-name-modal .cp-modal-footer .cp-btn-secondary'
  )[0];
  if (templateNameCancel) {
    templateNameCancel.click();
    return;
  }

  // 기준안 웹문서: 양식 전환·데이터 반영 등 확인 창이 남아 있으면 취소로 닫는다.
  const editorConfirms = resolveTutorialTargets(
    '.cp-evaluation-plan-web-doc-delete-modal, [data-tutorial-ep="unsaved-confirm-modal"]'
  );
  for (const box of editorConfirms) {
    const secondary = box.querySelector<HTMLElement>('.cp-modal-footer .cp-btn-secondary');
    if (secondary && isTutorialTargetVisible(secondary)) {
      secondary.click();
      return;
    }
  }
}

/** 중첩 모달의 바깥(backdrop) 클릭으로 그 중첩 모달만 닫을 때 쓰는 셀렉터 */
export const TUTORIAL_NESTED_OVERLAY_BACKDROP_SELECTOR = [
  '.cp-modal-backdrop',
  '.cp-profile-img-modal-overlay',
  '.cp-member-photo-upload-overlay',
  '.cp-class-settings-modal-dim',
  '.cp-apn-create-overlay-dim',
  '.cp-apn-check-modal-dim',
  '.cp-apn-modal-overlay',
  '.cp-apn-reach-overlay',
  '.cp-apn-tbl-overlay',
  '.cp-apn-incomplete-overlay',
  '.cp-apn-reward-result-overlay',
  '.cp-pin-bulk-overlay',
  '.cp-pin-tt-modal-overlay',
  '.cp-pin-cal-overlay',
  '.cp-pin-pts-overlay',
  '.cp-pin-confirm-overlay',
  '.cp-pc-manage-overlay',
  '.cp-weekly-learning-guide-modal-backdrop',
  '.cp-todo-note-item-add-overlay',
  '.cp-todo-share-modal-overlay',
  '.cp-budget-note-modal-overlay',
  '.cp-class-lesson-alarm-popup-overlay',
].join(', ');

const TUTORIAL_OVERLAY_ROOT_MATCH_SELECTOR = [
  '.cp-modal',
  '.cp-program-cross-popup',
  '.cp-program-note-popup',
  '.cp-program-unit-popup',
  '.cp-program-eval-dropdown',
  '.cp-class-picker-modal',
  '.cp-leave-modal-box',
  '[data-tutorial-color-modal-type]',
  '.cp-profile-img-modal',
  '.cp-member-photo-upload-modal',
  '.cp-hours-adjust-checklist-float',
  '.cp-apn-create-modal',
  '.cp-apn-set-modal',
  '.cp-apn-check-modal-panel',
  '.cp-apn-reach-modal',
  '.cp-apn-tbl-modal',
  '.cp-apn-incomplete-modal',
  '.cp-apn-reward-result-modal',
  '.cp-pin-settings-modal',
  '.cp-pin-item-modal',
  '.cp-pin-level-modal',
  '.cp-pin-bulk-modal',
  '.cp-pin-history-modal',
  '.cp-pin-store-modal',
  '.cp-pin-item-inv-modal',
  '.cp-pin-item-grant-modal',
  '.cp-pin-confirm',
  '.cp-pc-manage-modal',
  '.cp-weekly-learning-guide-modal',
  '.cp-todo-note-item-add-overlay',
  '.cp-todo-note-category-modal',
  '.cp-todo-share-modal',
  '.cp-budget-note-modal',
  '.cp-budget-note-meta-cols-menu-pop',
  '.cp-budget-note-row-menu-pop',
  '.cp-class-lesson-alarm-settings-root',
  '.cp-class-lesson-alarm-popup-overlay',
  '.cp-edu-timer-overlay',
  '.cp-slide-dice-overlay',
  '.cp-program-slide-dice-modal',
  '.cp-program-slide-bgm-modal',
  '.cp-program-slide-timer-modal',
  '[data-tutorial-pres="bgm-modal"]',
  '[data-tutorial-slide="timer-modal"]',
].join(', ');

/**
 * 타깃 요소 자체가 모달/팝업 루트일 때만 true.
 * 모달 *안의* 섹션·버튼까지 closest로 true가 되면, 스택 확장 시 부모 모달 전체가
 * 하이라이트에 합쳐져 파트별 안내가 깨진다.
 */
function isTutorialOverlayRootEl(el: HTMLElement): boolean {
  return el.matches(TUTORIAL_OVERLAY_ROOT_MATCH_SELECTOR);
}

export function mergeTutorialTargetEls(...groups: HTMLElement[][]): HTMLElement[] {
  const seen = new Set<HTMLElement>();
  const out: HTMLElement[] = [];
  for (const group of groups) {
    for (const el of group) {
      if (!el?.isConnected || seen.has(el)) continue;
      if (!isTutorialTargetVisible(el)) continue;
      seen.add(el);
      out.push(el);
    }
  }
  return out;
}

/**
 * 주 타깃보다 위에 쌓인(DOM상 뒤쪽) 오버레이만 반환한다.
 * 뒤에 가려진 미니 모달·팝업은 제외해, 전면 모달만 하이라이트·조작하게 한다.
 */
export function getTutorialStackedOverlaysAbove(primary: HTMLElement[]): HTMLElement[] {
  if (primary.length === 0) return [];
  let stacked: HTMLElement[] = [];
  try {
    stacked = resolveTutorialTargets(TUTORIAL_STACKED_OVERLAY_SELECTOR);
  } catch {
    stacked = [];
  }
  return stacked.filter((el) => {
    if (primary.some((p) => p === el || p.contains(el) || el.contains(p))) return false;
    return primary.some(
      (p) => (p.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
    );
  });
}

/**
 * 주 타깃이 모달/팝업이고 조작이 허용된 단계에서,
 * 그 위에 열린 다른 오버레이(중첩 모달)를 하이라이트 목록에 합친다.
 */
export function expandTutorialTargetsWithStackedOverlays(
  primary: HTMLElement[],
  options?: { includeStacked?: boolean }
): HTMLElement[] {
  if (!options?.includeStacked || primary.length === 0) return primary;
  if (!primary.some(isTutorialOverlayRootEl)) return primary;
  const extras = getTutorialStackedOverlaysAbove(primary);
  return mergeTutorialTargetEls(primary, extras);
}

/** 여러 타깃의 getBoundingClientRect를 합친 영역 */
export function unionTutorialTargetRect(
  targets: HTMLElement[]
): { top: number; left: number; width: number; height: number } | null {
  if (targets.length === 0) return null;
  let top = Infinity;
  let left = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  let any = false;
  for (const el of targets) {
    if (!el.isConnected) continue;
    const r = clientRectToLayoutRect(el.getBoundingClientRect());
    if (r.width <= 0 || r.height <= 0) continue;
    any = true;
    top = Math.min(top, r.top);
    left = Math.min(left, r.left);
    right = Math.max(right, r.right);
    bottom = Math.max(bottom, r.bottom);
  }
  if (!any) return null;
  return { top, left, width: right - left, height: bottom - top };
}

export type TutorialSpotlightHole = {
  top: number;
  left: number;
  width: number;
  height: number;
  radius: number;
};

/**
 * 타깃마다 개별 스포트라이트 구멍을 만든다.
 * - 여러 select/버튼이면 각각 둥근 구멍으로 모두 밝게 표시
 * - td 셀이면 안의 실제 컨트롤(select/input/button) 기준으로 맞춤
 * - 경계가 붙거나 거의 붙은 구멍은 하나로 합친다
 */
export function measureTutorialSpotlightHoles(
  targets: HTMLElement[],
  holePadding = 0,
  options?: { mergeNearby?: boolean }
): TutorialSpotlightHole[] {
  const mergeNearby = options?.mergeNearby !== false;
  const holes: TutorialSpotlightHole[] = [];
  for (const el of targets) {
    if (!el.isConnected) continue;
    const control =
      el.tagName === 'TD' || el.tagName === 'TH'
        ? (el.querySelector<HTMLElement>('select, input, button, textarea, .cp-period-toggle-btn') ??
          el)
        : el;
    const r = clientRectToLayoutRect(control.getBoundingClientRect());
    if (r.width <= 0 || r.height <= 0) continue;
    // 뷰포트 밖(스크롤로 가려진) 요소는 구멍에서 제외 — 딤이 잘못 뚫리는 것 방지
    const { width: vw, height: vh } = getLayoutViewportSize();
    if (r.bottom < 0 || r.right < 0 || r.top > vh || r.left > vw) continue;

    const style = window.getComputedStyle(control);
    const corners = [
      style.borderTopLeftRadius,
      style.borderTopRightRadius,
      style.borderBottomRightRadius,
      style.borderBottomLeftRadius,
    ];
    let radiusPx = 0;
    for (const raw of corners) {
      radiusPx = Math.max(radiusPx, parseCssLengthToPx(raw, Math.min(r.width, r.height)));
    }
    if (radiusPx <= 0) radiusPx = 8;

    const top = Math.max(0, r.top - holePadding);
    const left = Math.max(0, r.left - holePadding);
    const right = Math.min(vw, r.right + holePadding);
    const bottom = Math.min(vh, r.bottom + holePadding);
    const width = Math.max(0, right - left);
    const height = Math.max(0, bottom - top);
    if (width <= 0 || height <= 0) continue;

    holes.push(
      clipTutorialHoleToTableBodyVisibleArea(el, {
        top,
        left,
        width,
        height,
        radius: Math.min(radiusPx + holePadding, Math.min(width, height) / 2),
      })
    );
  }
  const visible = holes.filter((h) => h.width > 0 && h.height > 0);
  return mergeNearby ? mergeNearbyTutorialSpotlightHoles(visible) : visible;
}

/**
 * 테이블 tbody/행을 하이라이트할 때 sticky thead·스크롤 래퍼 밖(툴바 등)까지
 * 구멍이 뚫리지 않도록 보이는 본문 영역으로 자른다.
 * (가상 스크롤 spacer 때문에 tbody rect.top이 뷰포트 위로 크게 올라가 생기는 문제 포함)
 *
 * thead 안 타깃(머리글 버튼 등)에는 thead.bottom 클리핑을 적용하지 않는다.
 * 적용하면 버튼 영역이 잘려 하이라이트가 세로로 납작한 선처럼 보인다.
 */
function clipTutorialHoleToTableBodyVisibleArea(
  el: HTMLElement,
  hole: TutorialSpotlightHole
): TutorialSpotlightHole {
  const table = el.closest('table');
  if (!table) return hole;

  const thead = table.tHead ?? table.querySelector('thead');
  const inThead = Boolean(thead && (thead === el || thead.contains(el)));
  const wrap = el.closest(
    '.cp-annual-progress-table-wrap, .cp-program-progress-scroll-wrap, .cp-annual-timetable-table-wrap'
  );

  let top = hole.top;
  let left = hole.left;
  let right = hole.left + hole.width;
  let bottom = hole.top + hole.height;

  // 본문(tbody) 타깃만 sticky thead 아래로 자른다.
  if (thead && !inThead) {
    top = Math.max(top, clientRectToLayoutRect(thead.getBoundingClientRect()).bottom);
  }
  if (wrap) {
    const wr = clientRectToLayoutRect(wrap.getBoundingClientRect());
    top = Math.max(top, wr.top);
    left = Math.max(left, wr.left);
    right = Math.min(right, wr.right);
    bottom = Math.min(bottom, wr.bottom);
  }

  const width = Math.max(0, right - left);
  const height = Math.max(0, bottom - top);
  if (width <= 0 || height <= 0) {
    return { ...hole, width: 0, height: 0 };
  }
  return {
    top,
    left,
    width,
    height,
    radius: Math.min(hole.radius, Math.min(width, height) / 2),
  };
}

/** 구멍 사이 간격이 이 값 이하면 하나로 합침 (패딩·셀 간격으로 붙어 보이는 경우) */
const SPOTLIGHT_HOLE_MERGE_GAP_PX = 14;

function holesNearOrOverlapping(a: TutorialSpotlightHole, b: TutorialSpotlightHole, gap: number): boolean {
  const aRight = a.left + a.width;
  const aBottom = a.top + a.height;
  const bRight = b.left + b.width;
  const bBottom = b.top + b.height;
  const separatedX = aRight < b.left - gap || bRight < a.left - gap;
  const separatedY = aBottom < b.top - gap || bBottom < a.top - gap;
  return !separatedX && !separatedY;
}

function mergeTwoHoles(a: TutorialSpotlightHole, b: TutorialSpotlightHole): TutorialSpotlightHole {
  const top = Math.min(a.top, b.top);
  const left = Math.min(a.left, b.left);
  const right = Math.max(a.left + a.width, b.left + b.width);
  const bottom = Math.max(a.top + a.height, b.top + b.height);
  const width = right - left;
  const height = bottom - top;
  const radius = Math.min(Math.max(a.radius, b.radius), Math.min(width, height) / 2);
  return { top, left, width, height, radius };
}

/** 서로 겹치거나 거의 붙은 하이라이트를 반복적으로 합친다. */
export function mergeNearbyTutorialSpotlightHoles(
  holes: TutorialSpotlightHole[],
  gapPx = SPOTLIGHT_HOLE_MERGE_GAP_PX
): TutorialSpotlightHole[] {
  if (holes.length <= 1) return holes;
  let current = holes.slice();
  let merged = true;
  while (merged) {
    merged = false;
    const next: TutorialSpotlightHole[] = [];
    const used = new Set<number>();
    for (let i = 0; i < current.length; i += 1) {
      if (used.has(i)) continue;
      let acc = current[i];
      for (let j = i + 1; j < current.length; j += 1) {
        if (used.has(j)) continue;
        if (holesNearOrOverlapping(acc, current[j], gapPx)) {
          acc = mergeTwoHoles(acc, current[j]);
          used.add(j);
          merged = true;
        }
      }
      next.push(acc);
    }
    current = next;
  }
  return current;
}

/** 간격과 관계없이 모든 하이라이트를 하나의 사각형으로 합친다. */
export function unionAllTutorialSpotlightHoles(
  holes: TutorialSpotlightHole[]
): TutorialSpotlightHole[] {
  if (holes.length <= 1) return holes;
  return [holes.reduce((acc, hole) => mergeTwoHoles(acc, hole))];
}

/**
 * 타깃 요소(들)의 border-radius를 스포트라이트에 쓸 px 값으로 환산.
 * 여러 개면 가장 둥근 값을 쓰고, holePadding만큼 키워 테두리와 시각적으로 맞춘다.
 */
export function resolveTutorialSpotlightRadius(targets: HTMLElement[], holePadding = 0): number {
  if (targets.length === 0) return 14 + holePadding;
  let maxPx = 0;
  for (const el of targets) {
    if (!el.isConnected) continue;
    const r = clientRectToLayoutRect(el.getBoundingClientRect());
    if (r.width <= 0 || r.height <= 0) continue;
    const style = window.getComputedStyle(el);
    const corners = [
      style.borderTopLeftRadius,
      style.borderTopRightRadius,
      style.borderBottomRightRadius,
      style.borderBottomLeftRadius,
    ];
    for (const raw of corners) {
      maxPx = Math.max(maxPx, parseCssLengthToPx(raw, Math.min(r.width, r.height)));
    }
  }
  if (maxPx <= 0) return 14 + holePadding;
  return maxPx + holePadding;
}

function parseCssLengthToPx(value: string, percentBase: number): number {
  const v = (value || '').trim();
  if (!v || v === '0') return 0;
  // "12px" / "50%" / "999px" / "1.2rem" 등 — 공백으로 나뉜 복합값은 첫 토큰만 사용
  const token = v.split(/\s+/)[0];
  if (token.endsWith('%')) {
    const n = Number.parseFloat(token);
    if (!Number.isFinite(n)) return 0;
    return (n / 100) * percentBase;
  }
  if (token.endsWith('rem')) {
    const n = Number.parseFloat(token);
    if (!Number.isFinite(n)) return 0;
    const root = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
    return n * root;
  }
  if (token.endsWith('em')) {
    const n = Number.parseFloat(token);
    if (!Number.isFinite(n)) return 0;
    return n * 16;
  }
  const n = Number.parseFloat(token);
  return Number.isFinite(n) ? n : 0;
}
